import { Config } from "@config";
import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, Alert, Button } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Card } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import VisibilitySensor from "react-visibility-sensor";

interface Props extends MeasureComponentProps {
	portfolio?: string;
	borderless?: string;
}
interface State extends MeasureComponentState {
	loadingStatus: ProgressButtonState;
	feedData: BlogPost[];
}

interface BlogPost {
	channelTitle: string;
	channelLink: string;
	itemTitle: string;
	itemLink: string;
}

/**
 * @Shortcode
 *
 * @title "RSS Blogok"
 * @shortcode "rss_blogs"
 * @type "widget"
 * @lazyHydrate "whenVisible"
 * @propsList "[{"borderless":"true"},{"portfolio":"true"},{"borderless":"true","portfolio":"true"}]"
 */
export default class RssBlogs extends MeasureComponent<Props, State> {
	protected productTypes = "";
	protected readonly componentType = "widget";

	public constructor(props: Props) {
		super(props);

		this.state = { loadingStatus: undefined, wasVisible: false, feedData: [] };
	}

	private onFeedsLoad(): void {
		this.setState({
			loadingStatus: "loading",
		});

		http.post(Config.getWpRestApiUrl("/rss-blog"))
			.then((result) => {
				const { content } = result;

				this.setState({
					feedData: content,
					loadingStatus: "done",
				});
			})
			.catch((error: Error) => {
				this.setState({
					feedData: [],
					loadingStatus: "error",
				});

				logError(error);
			});
	}

	@autobind
	protected onVisibilityChange(isVisible: boolean): void {
		const { wasVisible } = this.state;

		super.onVisibilityChange(isVisible);

		if (isVisible && !wasVisible) {
			this.onFeedsLoad();
		}
	}

	public render(): ReactNode {
		const { wasVisible } = this.state;
		const { borderless } = this.props;

		if (borderless) {
			return (
				<VisibilitySensor
					{...GoogleAnalytics4.getVisibilitySensorProps()}
					active={!wasVisible}
					onChange={this.onVisibilityChange}
				>
					<div className="my-2 py-3 d-flex flex-column">
						<h2 className="h3">Blogajánló</h2>
						{this.renderFeeds()}
						{this.renderPortfolio()}
					</div>
				</VisibilitySensor>
			);
		}

		return (
			<VisibilitySensor
				{...GoogleAnalytics4.getVisibilitySensorProps()}
				active={!wasVisible}
				onChange={this.onVisibilityChange}
			>
				<Card className="my-2 p-3 bg-light">
					<h2 className="h3">Blogajánló</h2>
					{this.renderFeeds()}
					{this.renderPortfolio()}
				</Card>
			</VisibilitySensor>
		);
	}

	private renderFailure(): ReactNode {
		return (
			<Alert icon="auto" variant="danger">
				Hiba a blogok betöltése közben!
			</Alert>
		);
	}

	private renderPortfolio(): ReactNode {
		const { portfolio } = this.props;

		if (!portfolio) return null;

		return (
			<Button target="_blank" variant="light" href="https://www.portfolio.hu/blogger/" className="p-2 border-0">
				<img
					src="https://assets.portfolio.hu/images/portfolio-blogger.svg"
					width="136"
					height="68"
					alt="Portfolio Blogger"
					title="Portfolio Blogger"
					loading="eager"
				/>
			</Button>
		);
	}

	private renderFeeds(): ReactNode {
		const { loadingStatus, feedData } = this.state;

		if (!loadingStatus || loadingStatus === "loading") {
			return <Skeleton count={4} className="mb-2" height={50} />;
		}

		if (loadingStatus === "error" || !feedData?.length) {
			return this.renderFailure();
		}

		return feedData.map((data) => {
			const { channelTitle, channelLink, itemTitle, itemLink } = data;

			return (
				<Button
					className="mb-2 text-left text-dark p-2 border-0"
					variant="light"
					key={channelTitle + itemTitle}
					href={itemLink}
					target="_blank"
					rel="noreferrer nofollow"
				>
					<a
						className="badge badge-dark text-white text-decoration-none text-uppercase mb-1 mr-2"
						href={channelLink}
						target="_blank"
						rel="noreferrer nofollow"
					>
						{channelTitle}
					</a>
					<h3 className="h5 d-inline">{itemTitle}</h3>
				</Button>
			);
		});
	}

	protected getComponentName(): string {
		return "RssBlogs";
	}
}
